import type { NavigationGuard } from 'vue-router'
import { inject } from 'vue'
import { errorHandlerKey, servicesKey } from '@/config/keys.ts'
import { routeNames } from '@/router/names.ts'
import { onApiErrorHandler } from '@/helpers/backendErrors.ts'
import { useToast } from 'primevue'
import i18n from '@/i18n'
import { useAccountStore } from '@/stores/account.ts'
import { routeNamesByDocumentCode } from '@/config/requiredDocumentsCodes.ts'
import {
    findAssignedSubAccount,
    getAssignedSubAccountIds,
    requiresCurrentTermsAcceptance,
} from '@/helpers/guardianTerms.ts'

export const isChildProtectAcceptedGuard: NavigationGuard = async (to, from, next) => {
    const services = inject(servicesKey)
    const errorHandler = inject(errorHandlerKey)
    const toast = useToast()
    const accountStore = useAccountStore()

    try {
        const { childProtectAccepted } = await services!.account.getIsChildProtectAccepted()

        if (!childProtectAccepted) {
            return next()
        }

        toast.add({
            severity: 'success',
            summary: i18n.global.t('The document has already been approved'),
            life: 4000,
        })

        const { isSuccess, documentToAccept } = await accountStore.loadAccountData()

        if (documentToAccept) {
            return next({
                name: routeNamesByDocumentCode[documentToAccept],
            })
        }

        if (!isSuccess || !accountStore.accountData) {
            throw new Error('Cannot load account data in isChildProtectAcceptedGuard')
        }

        const targetSubAccountId = getAssignedSubAccountIds(accountStore.accountData)[0]

        if (!targetSubAccountId) {
            throw new Error('Missing assigned sub account in isChildProtectAcceptedGuard')
        }

        const assignedSubAccount = findAssignedSubAccount(
            accountStore.accountData,
            targetSubAccountId,
        )

        if (requiresCurrentTermsAcceptance(assignedSubAccount)) {
            return next({
                name: routeNames.GUARDIAN_TERMS_VIEW_NAME,
                params: {
                    subAccountId: targetSubAccountId,
                },
            })
        }

        return next({
            name: routeNames.HOME_VIEW_NAME,
            params: {
                subAccountId: targetSubAccountId,
            },
        })
    } catch (err) {
        onApiErrorHandler({
            err,
            toast,
            errorTitle: i18n.global.t('Error'),
            defaultErrorMessage: i18n.global.t(
                'There was a problem retrieving your account details',
            ),
            action: 'isChildProtectAcceptedGuard',
            errorHandler,
        })

        accountStore.logout(true)

        return next({
            name: routeNames.LOGIN_VIEW_NAME,
        })
    }
}
